import type { Random } from '../dice/random.ts';

/**
 * The seven Quick Generators, as the desktop's `scripting.Generators`: the same word lists,
 * and - for the same `java.util.Random` seed - the same names, cities and languages
 * (schema/fixtures/generator-cases.json; docs/PARITY-STEP-2-PLAN.md decision 4). Scripts
 * reach them as `generate_name()` and the rest, beside `rand_choice` and `rand_int`.
 */

const GIVEN_NAMES = ['Arden', 'Bryn', 'Coren', 'Dessa', 'Eamon', 'Fenna', 'Garrick', 'Halsin', 'Ilya', 'Joric', 'Kessa', 'Lorne', 'Maren', 'Niall', 'Orys', 'Perrin', 'Quenna', 'Roran', 'Selwyn', 'Talia', 'Ulric', 'Vesna', 'Wynne', 'Yorick', 'Zara'];
const SURNAMES = ['Ashford', 'Blackwood', 'Corvane', 'Dunmoor', 'Emberlyn', 'Fairwind', 'Graythorn', 'Hollowell', 'Ironbrook', 'Larkspur', 'Merrow', 'Nightshade', 'Oakhart', 'Ravenscar', 'Stormwake', 'Thistledown', 'Vaelthorn', 'Wintermere'];

const CITY_PREFIXES = ['Silver', 'Iron', 'Wolf', 'Raven', 'Stone', 'Storm', 'Amber', 'Shadow', 'Winter', 'Sun', 'Grey', 'High', 'Deep', 'Thorn', 'Moon'];
const CITY_SUFFIXES = ['hold', 'keep', 'haven', 'reach', 'watch', 'gate', 'spire', 'ford', 'crest', 'fall', 'mere', 'hollow', 'bridge'];
const CITY_FEATURES = [
  'a fortified harbor that never freezes',
  'a market square built over an older, buried city',
  'walls quarried from a single vein of black stone',
  'a river that runs backward every spring flood',
  'seven bridges, each guarded by a different noble house',
  'a bell tower that rings on its own before disasters',
  'catacombs older than the city above them',
  'an academy famous for cartographers',
  'a standing garrison left over from a war two centuries gone',
  "terraced vineyards climbing the city's inner walls",
];

const NPC_ROLES = ['innkeeper', 'blacksmith', 'hedge wizard', 'caravan guard', 'retired soldier', 'smuggler', 'town clerk', 'traveling bard', 'herbalist', 'tomb robber', 'temple acolyte', 'harbor pilot', 'moneylender', 'cartographer', 'beast handler'];
const NPC_TRAITS = [
  'never makes eye contact',
  'counts everything out loud',
  "collects other people's debts",
  'speaks three languages badly',
  'always knows the fastest way out of a room',
  "is convinced they're cursed",
  'hums the same six notes when nervous',
  "keeps a knife nobody's ever seen used",
  "remembers every name they've ever heard",
  'flinches at the sound of bells',
];
const NPC_MOTIVATIONS = [
  "paying off a debt they won't discuss",
  "protecting a family no one's met",
  'searching for someone who vanished years ago',
  'trying to buy passage out of the region',
  'quietly working against their own employer',
  'atoning for something done during the last war',
  'building a reputation to hide an old one',
  'waiting for a specific ship to come in',
];

const FACTION_PREFIXES = ['The Order of', 'The Covenant of', 'The Free', 'The Iron', 'The Silent', 'The Last', 'The Sworn', 'The Hollow', 'The Gilded', 'The Wandering'];
const FACTION_NOUNS = ['Flame', 'Blade', 'Compass', 'Ledger', 'Tide', 'Crown', 'Veil', 'Hound', 'Anvil', 'Star', 'Root', 'Chain'];
const FACTION_GOALS = [
  "control of the region's trade routes",
  'the overthrow of a specific ruling house',
  'the protection of a secret no one else can be trusted with',
  'the recovery of something stolen generations ago',
  'independence from a distant empire',
  'the spread of a belief the established temples call heresy',
  'revenge for a betrayal only its oldest members remember',
];

const TAVERN_PREFIXES = ['The Prancing', 'The Rusty', 'The Drunken', 'The Weary', 'The Gilded', 'The Broken', 'The Laughing', 'The Salty', 'The Wandering', 'The Crooked'];
const TAVERN_NOUNS = ['Pony', 'Anchor', 'Dragon', 'Traveler', 'Griffin', 'Lantern', 'Boar', 'Kettle', 'Crow', 'Barrel'];
const TAVERN_SPECIALTIES = [
  "a spiced cider no one's been able to copy",
  'a back room where every deal in town eventually happens',
  'a house band that only plays after midnight',
  "the best (and only) hot meal for a day's ride",
  'a cellar rumored to connect to the old sewers',
  "a landlord who remembers every regular's usual order",
];
const TAVERN_ATMOSPHERES = [
  'loud, smoky, and cheerfully overcrowded',
  'quiet enough to hear the fire crackle',
  'rough around the edges but fiercely loyal to its regulars',
  "surprisingly upscale for the neighborhood it's in",
  'the kind of place travelers are warned about first',
];

const HISTORY_SUBJECTS = ['a founding', 'a siege', 'a plague', 'a coronation', 'a betrayal', 'a great fire', 'a treaty', 'a schism', 'a discovery', 'an uprising', 'a famine', 'a first contact'];
const HISTORY_CONSEQUENCES = [
  "reshaped the region's borders for a generation",
  'is still commemorated every year, for better or worse',
  'was covered up by those in power at the time',
  'gave rise to a faction that persists to this day',
  'is remembered very differently by each side involved',
  'left behind ruins that are still avoided',
  'changed the balance of power between two rival houses',
];

const LANGUAGE_FAMILY_ADJECTIVES = ['Old', 'High', 'Low', 'Deep', 'Wind', 'Salt', 'Stone', 'Ash'];
const LANGUAGE_FAMILY_NOUNS = ['Tongue', 'Speech', 'Cant', 'Dialect', 'Rite-Speech'];
const SYLLABLES = ['va', 'ren', 'tho', 'sil', 'ka', 'mir', 'us', 'en', 'dol', 'ith', 'or', 'an', 'qua', 'zel', 'yn'];

/** What a generator makes: a title, a description, and fields of its own (population, goal...). */
export type Generated = Record<string, string | number | string[]>;

const pick = <T>(random: Random, options: readonly T[]): T => options[random.nextInt(options.length)]!;
const capitalize = (text: string) => (text === '' ? text : text[0]!.toUpperCase() + text.slice(1));

export function generateName(random: Random): string {
  return `${pick(random, GIVEN_NAMES)} ${pick(random, SURNAMES)}`;
}

export function generateCity(random: Random): Generated {
  const name = pick(random, CITY_PREFIXES) + pick(random, CITY_SUFFIXES);
  const feature = pick(random, CITY_FEATURES);
  const population = 500 + random.nextInt(60) * 500;
  return { title: name, population, notableFeature: feature, description: `A settlement of roughly ${population} people, known for ${feature}.` };
}

export function generateNpc(random: Random): Generated {
  const name = generateName(random);
  const role = pick(random, NPC_ROLES);
  const trait = pick(random, NPC_TRAITS);
  const motivation = pick(random, NPC_MOTIVATIONS);
  return { title: name, role, trait, motivation, description: `A ${role} who ${trait}, secretly ${motivation}.` };
}

export function generateFaction(random: Random): Generated {
  const name = `${pick(random, FACTION_PREFIXES)} ${pick(random, FACTION_NOUNS)}`;
  const goal = pick(random, FACTION_GOALS);
  return { title: name, goal, description: `${name} is a faction working toward ${goal}.` };
}

export function generateTavern(random: Random): Generated {
  const name = `${pick(random, TAVERN_PREFIXES)} ${pick(random, TAVERN_NOUNS)}`;
  const specialty = pick(random, TAVERN_SPECIALTIES);
  const atmosphere = pick(random, TAVERN_ATMOSPHERES);
  return { title: name, specialty, atmosphere, description: `${name} is known for ${specialty}. The atmosphere is ${atmosphere}.` };
}

export function generateHistory(random: Random): Generated {
  const subject = pick(random, HISTORY_SUBJECTS);
  const consequence = pick(random, HISTORY_CONSEQUENCES);
  return { title: `${capitalize(subject)} That ${capitalize(consequence)}`, description: `A historical event: ${subject} that ${consequence}.` };
}

export function generateLanguage(random: Random): Generated {
  const name = `${pick(random, LANGUAGE_FAMILY_ADJECTIVES)} ${pick(random, LANGUAGE_FAMILY_NOUNS)}`;
  const sampleWords = Array.from({ length: 5 }, () => randomWord(random));
  return { title: name, sampleWords, description: `A constructed language. Sample words: ${sampleWords.join(', ')}.` };
}

function randomWord(random: Random): string {
  const syllableCount = 2 + random.nextInt(2);
  let word = '';
  for (let i = 0; i < syllableCount; i++) word += pick(random, SYLLABLES);
  return capitalize(word);
}

/** A script's `rand_choice(list)`: which of `size` items it picks. */
export function choiceIndex(random: Random, size: number): number {
  return Math.min(Math.trunc(random.nextDouble() * size), size - 1);
}

/** A script's `rand_int(min, max)`: inclusive at both ends. As Java, a range that doesn't fit an int wraps, and an empty one fails. */
export function randInt(random: Random, min: number, max: number): number {
  const bound = Number(BigInt.asIntN(32, BigInt(max) - BigInt(min) + 1n));
  if (bound <= 0) throw new RangeError(`rand_int(${min}, ${max}) has no numbers to pick from.`);
  return min + random.nextInt(bound);
}

/** The generators, by the GeneratorKey names the desktop stores and packs use, in its order. */
export const GENERATOR_KEYS = ['NAME', 'CITY', 'NPC', 'FACTION', 'TAVERN', 'HISTORY', 'LANGUAGE'] as const;
export type GeneratorKey = (typeof GENERATOR_KEYS)[number];

export interface GeneratorInfo {
  readonly key: GeneratorKey;
  /** As the desktop's GeneratorKey.displayName(). */
  readonly name: string;
  /** What a script calls it. */
  readonly functionName: string;
  /** The entry type a result becomes, as the desktop's Quick Generators. */
  readonly entityType: string;
  readonly generate: (random: Random) => Generated;
}

export const GENERATORS: Readonly<Record<GeneratorKey, GeneratorInfo>> = {
  NAME: { key: 'NAME', name: 'Name', functionName: 'generate_name', entityType: 'PERSON', generate: (random) => ({ title: generateName(random), description: '' }) },
  CITY: { key: 'CITY', name: 'City', functionName: 'generate_city', entityType: 'PLACE', generate: generateCity },
  NPC: { key: 'NPC', name: 'NPC', functionName: 'generate_npc', entityType: 'PERSON', generate: generateNpc },
  FACTION: { key: 'FACTION', name: 'Faction', functionName: 'generate_faction', entityType: 'FACTION', generate: generateFaction },
  TAVERN: { key: 'TAVERN', name: 'Tavern', functionName: 'generate_tavern', entityType: 'PLACE', generate: generateTavern },
  HISTORY: { key: 'HISTORY', name: 'History', functionName: 'generate_history', entityType: 'EVENT', generate: generateHistory },
  LANGUAGE: { key: 'LANGUAGE', name: 'Language', functionName: 'generate_language', entityType: 'CONCEPT', generate: generateLanguage },
};
